import React, { FunctionComponent, ReactNode } from 'react'
import { Markup } from 'interweave'
import Modal, { ModalClosingButton } from '../modal'
import style from './infoModalInteriorCombination.css'
import { InfoModalData } from '../../entities/InfoModal'
import Tabbox, { Tab } from '../tabbox'
import PackageTab from '../package-tab'
import useTranslation from '../../hooks/useTranslation'
import Gallery from '../gallery'
import Media from '../../entities/Media'
import { ExtendedMedia } from '../../entities/Equipment'
import { InfoModalTypes } from '../../entities/InfoModalType'

export type Props = InfoModalData & {
    closeInfoModal: Function
}

const InfoModalInteriorCombination: FunctionComponent<Props> = (props) => {
    const {
        activeModalType,
        title,
        content,
        media,
        activeMedia,
        extendedPackageContent,
        extendedMedia,
        closeInfoModal,
    } = props

    const { t } = useTranslation()

    if (activeModalType !== InfoModalTypes.INTERIOR_COMBINATION) {
        return null
    }

    const hasExtendedMedia = extendedMedia && Object.entries(extendedMedia).length > 0
    const closeButtonClicked = (): void => {
        closeInfoModal()
    }

    const getImageMediaFrom = (mediaList: Media[], forced: boolean) => {
        return mediaList
            .filter(({ type }) => type === 'image')
            .filter((mediaItem) => mediaItem.forced === forced)
            .map(({ path }) => ({
                src: path,
                width: undefined,
                height: undefined,
            }))
    }

    const getTextMediaElements = (mediaList: Media[]): ReactNode[] => {
        return mediaList
            .filter(({ type }) => type === 'text')
            .filter((mediaItem) => !mediaItem.forced)
            .map((textItem) =>
                <div key={textItem.id}
                     className={style.textItem}>
                    <Markup content={textItem.text} />
                </div>)
    }

    const getForcedTextMediaElements = (mediaList: Media[]): ReactNode[] => {
        return mediaList
            .filter((mediaItem) => mediaItem.type === 'text')
            .filter((mediaItem) => mediaItem.forced)
            .map((textItem) => {
                const label = textItem.label ? `<strong>${textItem.label}</strong><br>` : ''
                const text = textItem.text ? textItem.text.replaceAll('\r\n', '<br>') : textItem.text
                return <div key={textItem.id} className={`${style.textItem} ${style.forced}`}>
                    <Markup content={`${label}${text}`} />
                </div>
            })
    }

    const createForcedImageTab = (): Tab | null => {
        const imageMedia = getImageMediaFrom(media, true)

        if (imageMedia.length === 0 && !hasExtendedMedia) {
            return null
        }

        const contentBlocks: ReactNode[] = []

        if (imageMedia.length > 0) {
            contentBlocks.push(
                <Gallery
                    thumbnails={imageMedia}
                    images={imageMedia}
                    miwiImagesUrls={[imageMedia[0].src]}
                    forced={true}
                />,
            )
        }

        if (hasExtendedMedia) {
            Object.entries(extendedMedia).forEach(([key, value]: [string, ExtendedMedia]) => {
                const extImages = getImageMediaFrom(value.media, true)

                if (extImages.length > 0) {
                    contentBlocks.push(
                        <div className={`${style.extendedImageItem} ${style.forcedExtendedImage}`} key={`image-${key}`}>
                            <h4>{`${t('infoModal.forcedImage')} ${value.evaKey}`}</h4>
                            <Gallery
                                thumbnails={extImages}
                                images={extImages}
                                miwiImagesUrls={[extImages[0].src]}
                                forced={true}
                            />
                        </div>,
                    )
                }
            })
        }

        return contentBlocks.length === 0
            ? null
            : {
                id: 'forcedImage',
                label: t('infoModal.forcedImage'),
                content: <>{contentBlocks}</>,
                isActive: activeMedia === 'forcedImage',
            }

    }

    const createForcedTextTab = (): Tab | null => {
        const forcedTextMediaElements = getForcedTextMediaElements(media)

        if (forcedTextMediaElements.length === 0 && !hasExtendedMedia) {
            return null
        }

        const contentBlocks: React.ReactNode[] = []

        if (forcedTextMediaElements.length > 0) {
            contentBlocks.push(
                <div className={style.texts}>
                    {forcedTextMediaElements}
                </div>,
            )
        }

        if (hasExtendedMedia) {
            Object.entries(extendedMedia).forEach(([key, value]: [string, ExtendedMedia]) => {
                const forcedExtTextsElements = getForcedTextMediaElements(value.media)

                if (forcedExtTextsElements.length > 0) {
                    contentBlocks.push(
                        <div key={key} className={style.extendedTextItem}>
                            <h4>{`${t('infoModal.forcedText')} ${value.evaKey}`}</h4>
                            {forcedExtTextsElements}
                        </div>,
                    )
                }
            })
        }

        return contentBlocks.length === 0
            ? null
            : {
                id: 'forcedText',
                label: t('infoModal.forcedText'),
                content: (
                    <div className={style.texts}>
                        <>{contentBlocks}</>
                    </div>
                ),
            }

    }

    const createTextTab = (): Tab | null => {
        const textMediaElements = getTextMediaElements(media)

        if (textMediaElements.length === 0 && !hasExtendedMedia) {
            return null
        }

        const contentBlocks: React.ReactNode[] = []

        if (textMediaElements.length > 0) {
            contentBlocks.push(
                <div className={style.texts}>
                    {textMediaElements}
                </div>,
            )
        }

        if (hasExtendedMedia) {
            Object.entries(extendedMedia).forEach(([key, value]: [string, ExtendedMedia]) => {
                const extTextsElements = getTextMediaElements(value.media)

                if (extTextsElements.length > 0) {
                    contentBlocks.push(
                        <div key={key} className={style.extendedTextItem}>
                            <h4>{`${t('infoModal.text')} ${value.evaKey}`}</h4>
                            {extTextsElements}
                        </div>,
                    )
                }
            })
        }

        return contentBlocks.length === 0
            ? null
            : {
                id: 'text',
                label: t('infoModal.text'),
                content: (
                    <div className={style.texts}>
                        <>{contentBlocks}</>
                    </div>
                ),
            }
    }

    const createImageTab = (): Tab | null => {
        const imageMedia = getImageMediaFrom(media,false)

        if (imageMedia.length === 0 && !hasExtendedMedia) {
            return null
        }

        const contentBlocks: ReactNode[] = []

        if (imageMedia.length > 0) {
            contentBlocks.push(
                <Gallery
                    key="image"
                    thumbnails={imageMedia}
                    images={imageMedia}
                    miwiImagesUrls={[imageMedia[0].src]}
                />,
            )
        }

        if (hasExtendedMedia) {
            Object.entries(extendedMedia).forEach(([key, value]: [string, ExtendedMedia]) => {
                const extImages = getImageMediaFrom(value.media, false)

                if (extImages.length > 0) {
                    contentBlocks.push(
                        <div className={style.extendedImageItem} key={`image-${key}`}>
                            <h4>{`${t('infoModal.image')} ${value.evaKey}`}</h4>
                            <Gallery
                                thumbnails={extImages}
                                images={extImages}
                                miwiImagesUrls={[extImages[0].src]}
                            />
                        </div>,
                    )
                }
            })
        }

        return contentBlocks.length === 0
            ? null
            : {
                id: 'image',
                label: t('infoModal.image'),
                content: <>{contentBlocks}</>,
            }
    }

    const createPackageTab = (): Tab | null => {
        if (!content || content.length === 0) {
            return null
        }

        return {
            id: 'package',
            label: t('infoModal.package.tab'),
            content: <PackageTab content={content} />,
        }
    }

    const createExtendedPackageTabs = (): Tab[] | null => {
        if (!extendedPackageContent || Object.entries(extendedPackageContent).length === 0) {
            return []
        }

        return Object.entries(extendedPackageContent).map(([key, value]) => ({
            id: `package-${key}`,
            label: `${t('infoModal.package.tab')} ${value.evaKey}`,
            content: <PackageTab content={value.content} />,
        }))
    }

    const tabBoxTabs: Tab[] = [
        createForcedImageTab(),
        createForcedTextTab(),
        createTextTab(),
        createImageTab(),
        createPackageTab(),
        ...createExtendedPackageTabs(),
    ].filter(Boolean).map((tab) => ({
        ...tab,
        content: <div className={style.infoTabWrapper}>{tab?.content}</div>,
    }))

    return (
        <Modal
            isVisible={true}
            onClose={closeButtonClicked}
            contentClassName={style.modalContent}
        >
            <div className={style.closingButtonWrapper}>
                <ModalClosingButton onClick={closeButtonClicked} className={style.closingButton} />
            </div>
            <article className={style.container}>
                <h2 className={style.title}>
                    <Markup content={title} />
                </h2>
                <Tabbox tabs={tabBoxTabs} />
            </article>
        </Modal>
    )
}

export default InfoModalInteriorCombination
